import { motion } from 'framer-motion';
import { FileText, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/landing/Footer';

const lastUpdated = 'April 9, 2026';

const tableOfContents = [
  { id: 'overview', title: '1. Overview' },
  { id: 'services', title: '2. Services Provided' },
  { id: 'accounts', title: '3. Accounts & Eligibility' },
  { id: 'payment', title: '4. Subscriptions, Payment & Refunds' },
  { id: 'customer-responsibilities', title: '5. Customer Responsibilities' },
  { id: 'acceptable-use', title: '6. Acceptable Use' },
  { id: 'third-party-software', title: '7. Third-Party Software & Services' },
  { id: 'cloud-disclaimer', title: '8. Cloud Provider Disclaimer' },
  { id: 'liability', title: '9. Limitation of Liability' },
  { id: 'warranties', title: '10. Disclaimer of Warranties' },
  { id: 'indemnification', title: '11. Indemnification' },
  { id: 'termination', title: '12. Suspension & Termination' },
  { id: 'disputes', title: '13. Dispute Resolution' },
  { id: 'governing-law', title: '14. Governing Law' },
  { id: 'changes', title: '15. Changes to These Terms' },
  { id: 'contact', title: '16. Contact' },
];

export function TermsPage() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-20">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/60 to-teal-50/60" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="h-16 w-16 rounded-full gradient-primary flex items-center justify-center mx-auto mb-6">
                <FileText className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Service</h1>
              <p className="text-muted-foreground">Last updated: {lastUpdated}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16 flex-1">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-[280px_1fr] gap-8">
              <motion.aside
                className="lg:sticky lg:top-24 lg:self-start"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Card>
                  <CardContent className="p-4">
                    <h2 className="font-semibold mb-3 text-sm uppercase tracking-wider text-muted-foreground">
                      Table of Contents
                    </h2>
                    <nav className="space-y-1">
                      {tableOfContents.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => scrollToSection(item.id)}
                          className="flex items-center w-full text-left text-sm py-2 px-3 rounded-md hover:bg-muted transition-colors group"
                        >
                          <ChevronRight className="h-4 w-4 mr-2 text-muted-foreground group-hover:text-emerald-600" />
                          <span className="group-hover:text-emerald-600">{item.title}</span>
                        </button>
                      ))}
                    </nav>
                  </CardContent>
                </Card>
              </motion.aside>

              <motion.div
                className="prose prose-slate max-w-none"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <Card>
                  <CardContent className="p-6 md:p-8 space-y-8">
                    <section id="overview">
                      <h2 className="text-2xl font-bold mb-4">1. Overview</h2>
                      <p className="text-muted-foreground">
                        These Terms of Service ("Terms") govern your access to and use of
                        SetOpenClaw — a hosted control plane that lets you provision, configure, and
                        manage AI assistant instances built on the open-source OpenClaw project. By
                        creating an account or using the Services, you agree to be bound by these
                        Terms.
                      </p>
                    </section>

                    <section id="services">
                      <h2 className="text-2xl font-bold mb-4">2. Services Provided</h2>
                      <p className="text-muted-foreground mb-4">SetOpenClaw provides:</p>
                      <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                        <li>One-click deployment of OpenClaw assistant instances to supported cloud providers.</li>
                        <li>Configuration of channel integrations (Telegram, Slack, Discord, WhatsApp, and others).</li>
                        <li>Instance health monitoring, alerts, and automatic backups.</li>
                        <li>Subscription billing, usage metering, and team management.</li>
                        <li>Optional add-ons such as marketplace skills and analytics.</li>
                      </ul>
                      <p className="text-muted-foreground mt-4">
                        We may modify, add, or remove features at any time. We will give reasonable
                        notice before removing functionality that is materially relied upon by paying
                        customers.
                      </p>
                    </section>

                    <section id="accounts">
                      <h2 className="text-2xl font-bold mb-4">3. Accounts & Eligibility</h2>
                      <p className="text-muted-foreground">
                        You must be at least 18 years old (or the age of majority in your
                        jurisdiction) to use SetOpenClaw. You are responsible for maintaining the
                        confidentiality of your account credentials and for all activity under your
                        account. Notify us promptly at{' '}
                        <a href="mailto:support@setopenclaw.com" className="text-emerald-600 hover:underline">
                          support@setopenclaw.com
                        </a>{' '}
                        if you suspect unauthorized access.
                      </p>
                    </section>

                    <section id="payment">
                      <h2 className="text-2xl font-bold mb-4">4. Subscriptions, Payment & Refunds</h2>
                      <p className="text-muted-foreground mb-4">
                        SetOpenClaw is offered on monthly recurring subscription plans, billed in
                        advance through Stripe. You authorize us to charge your payment method on
                        the renewal date for each billing cycle until you cancel.
                      </p>
                      <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                        <li>Subscription fees are non-refundable for partial billing periods.</li>
                        <li>You may cancel at any time from the dashboard. Cancellation takes effect at the end of the current billing period.</li>
                        <li>Cloud provider and AI provider charges are billed directly by those providers and are not part of your SetOpenClaw subscription.</li>
                        <li>We may change pricing with at least 30 days' notice for renewing subscriptions.</li>
                      </ul>
                    </section>

                    <section id="customer-responsibilities">
                      <h2 className="text-2xl font-bold mb-4">5. Customer Responsibilities</h2>
                      <p className="text-muted-foreground mb-4">You are responsible for:</p>
                      <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                        <li>Backing up any data you store on or process through your deployed instances.</li>
                        <li>Providing accurate cloud provider credentials and ensuring they grant the permissions required for the deployments you request.</li>
                        <li>Compliance with the terms of any cloud provider, AI provider, or messaging channel you connect.</li>
                        <li>The content you process through your assistant and how it is used.</li>
                        <li>Compliance with all applicable laws and regulations in the jurisdictions where you operate.</li>
                      </ul>
                    </section>

                    <section id="acceptable-use">
                      <h2 className="text-2xl font-bold mb-4">6. Acceptable Use</h2>
                      <p className="text-muted-foreground mb-4">You may not use SetOpenClaw to:</p>
                      <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                        <li>Violate any law or infringe the rights of others.</li>
                        <li>Send spam, phishing, or other unsolicited messages.</li>
                        <li>Build or operate assistants designed to deceive users about the nature of the bot.</li>
                        <li>Generate or distribute illegal, harmful, or abusive content, including child sexual abuse material.</li>
                        <li>Attempt to circumvent rate limits, billing, or security controls.</li>
                        <li>Reverse-engineer, scrape, or attempt unauthorized access to the SetOpenClaw control plane.</li>
                      </ul>
                      <p className="text-muted-foreground mt-4">
                        We may suspend or terminate accounts that violate these rules, with or without notice.
                      </p>
                    </section>

                    <section id="third-party-software">
                      <h2 className="text-2xl font-bold mb-4">7. Third-Party Software & Services</h2>
                      <p className="text-muted-foreground">
                        OpenClaw is open-source software developed and maintained by third parties.
                        SetOpenClaw is not the developer, owner, or maintainer of OpenClaw and does
                        not guarantee its functionality, security, availability, or continued
                        development. Issues arising from OpenClaw itself, its updates, or other
                        third-party integrations (cloud providers, AI providers, messaging channels)
                        are outside SetOpenClaw's liability scope. Use is at your own risk and
                        subject to the respective licenses and terms.
                      </p>
                    </section>

                    <section id="cloud-disclaimer">
                      <h2 className="text-2xl font-bold mb-4">8. Cloud Provider Disclaimer</h2>
                      <p className="text-muted-foreground">
                        Your deployments run on cloud infrastructure operated by third parties (such
                        as AWS, Google Cloud, Azure, or Hetzner) that you select and pay for
                        directly. SetOpenClaw is not responsible for cloud provider outages, billing
                        disputes, region-specific restrictions, or quota limits. We will make
                        reasonable efforts to surface provider errors in the dashboard, but we
                        cannot guarantee provider uptime.
                      </p>
                    </section>

                    <section id="liability">
                      <h2 className="text-2xl font-bold mb-4">9. Limitation of Liability</h2>
                      <p className="text-muted-foreground uppercase text-sm">
                        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, SETOPENCLAW AND ITS
                        OWNERS, EMPLOYEES, AND CONTRACTORS SHALL NOT BE LIABLE FOR ANY INDIRECT,
                        INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT
                        LIMITED TO LOSS OF DATA, LOSS OF REVENUE, LOSS OF PROFITS, BUSINESS
                        INTERRUPTION, UNAUTHORIZED ACCESS TO YOUR SYSTEMS OR DATA, SECURITY
                        BREACHES, OR DAMAGES ARISING FROM THIRD-PARTY SOFTWARE, CLOUD PROVIDERS, OR
                        AI PROVIDERS, REGARDLESS OF THE CAUSE OF ACTION OR THE THEORY OF LIABILITY.
                      </p>
                      <p className="text-muted-foreground mt-4">
                        SetOpenClaw's total aggregate liability for any claims arising out of or
                        related to these Terms shall not exceed the amount paid by you to
                        SetOpenClaw in the twelve (12) months preceding the event giving rise to the
                        claim.
                      </p>
                    </section>

                    <section id="warranties">
                      <h2 className="text-2xl font-bold mb-4">10. Disclaimer of Warranties</h2>
                      <p className="text-muted-foreground uppercase text-sm">
                        THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF
                        ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING BUT NOT LIMITED
                        TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
                        AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICES WILL BE
                        ERROR-FREE, THAT YOUR DEPLOYMENTS WILL OPERATE WITHOUT INTERRUPTION, OR THAT
                        ANY THIRD-PARTY SOFTWARE WILL MEET YOUR SPECIFIC REQUIREMENTS.
                      </p>
                    </section>

                    <section id="indemnification">
                      <h2 className="text-2xl font-bold mb-4">11. Indemnification</h2>
                      <p className="text-muted-foreground">
                        You agree to indemnify, defend, and hold harmless SetOpenClaw and its
                        owners, employees, and contractors from any claims, damages, losses,
                        liabilities, costs, and expenses (including reasonable attorneys' fees)
                        arising from: (a) your use of the Services; (b) your violation of these
                        Terms; (c) your violation of any applicable law; or (d) third-party claims
                        related to the content you process or the use of the assistants you deploy.
                      </p>
                    </section>

                    <section id="termination">
                      <h2 className="text-2xl font-bold mb-4">12. Suspension & Termination</h2>
                      <p className="text-muted-foreground">
                        We may suspend or terminate your access to the Services if you violate these
                        Terms, fail to pay subscription fees, or engage in activity that risks harm
                        to other users or the platform. You may terminate your account at any time
                        from the dashboard. On termination, your deployed instances will be torn
                        down according to the schedule on your Data Deletion request — see our{' '}
                        <a href="/data-deletion" className="text-emerald-600 hover:underline">
                          Data Deletion Policy
                        </a>{' '}
                        for details.
                      </p>
                    </section>

                    <section id="disputes">
                      <h2 className="text-2xl font-bold mb-4">13. Dispute Resolution</h2>
                      <p className="text-muted-foreground">
                        Disputes shall first be resolved through good-faith negotiation. If
                        unresolved within 30 days, disputes shall be resolved through binding
                        arbitration administered in accordance with the rules of a recognized
                        arbitration body in the governing-law jurisdiction. Each party bears its own
                        costs. Disputes proceed on an individual basis, not as a class action.
                      </p>
                    </section>

                    <section id="governing-law">
                      <h2 className="text-2xl font-bold mb-4">14. Governing Law</h2>
                      <p className="text-muted-foreground">
                        These Terms shall be governed by and construed in accordance with the laws
                        of the jurisdiction in which SetOpenClaw is operated, without regard to
                        conflict of laws principles.
                      </p>
                    </section>

                    <section id="changes">
                      <h2 className="text-2xl font-bold mb-4">15. Changes to These Terms</h2>
                      <p className="text-muted-foreground">
                        We may update these Terms at any time. Changes will be posted with an
                        updated date. Continued use of the Services after changes constitutes
                        acceptance of the updated Terms.
                      </p>
                    </section>

                    <section id="contact">
                      <h2 className="text-2xl font-bold mb-4">16. Contact</h2>
                      <p className="text-muted-foreground">
                        Questions about these Terms? Reach us at{' '}
                        <a
                          href="mailto:support@setopenclaw.com"
                          className="text-emerald-600 hover:underline"
                        >
                          support@setopenclaw.com
                        </a>
                        .
                      </p>
                    </section>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
